import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import Lenis from "lenis";
import Welcome from "@/pages/Welcome";
import Gate from "@/pages/Gate";
import Home from "@/pages/Home";
import Profil from "@/pages/Profil";
import Admin from "@/pages/Admin";
import Instant from "@/pages/Instant";
import Session from "@/pages/Session";
import Sos from "@/pages/Sos";
import MonInstant from "@/pages/MonInstant";
import Kids from "@/pages/Kids";
import Explorer from "@/pages/Explorer";
import Intervenants from "@/pages/Intervenants";
import Echanger from "@/pages/Echanger";
import Reservation from "@/pages/Reservation";
import Legal from "@/pages/Legal";
import Visio from "@/pages/Visio";
import Offres from "@/pages/Offres";
import Boutique from "@/pages/Boutique";
import MonAccompagnement from "@/pages/MonAccompagnement";
import PaiementSucces from "@/pages/PaiementSucces";
import Connexion from "@/pages/Connexion";
import PartnerLink from "@/pages/PartnerLink";
import PartnerSignup from "@/pages/PartnerSignup";
import PartnerSpace from "@/pages/PartnerSpace";
import TopBar from "@/components/julie/TopBar";
import BottomNav from "@/components/julie/BottomNav";
import InstallPrompt from "@/components/julie/InstallPrompt";

function App() {
  useEffect(() => {
    // Lenis (défilement fluide) uniquement sur ordinateur : sur téléphone il figeait la page —
    // le défilement natif tactile est déjà fluide et bien plus fiable.
    const isTouch = window.matchMedia("(pointer: coarse)").matches;
    if (isTouch) return;
    const lenis = new Lenis({ lerp: 0.085, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const loop = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

function AppContent() {
  const location = useLocation();

  // Chaque page s'ouvre toujours en haut (jamais au milieu du défilement précédent)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Règle de saisie sur TOUT le site : 1re lettre en majuscule, le reste en minuscules.
  // Exception : toute adresse e-mail est entièrement en minuscules.
  useEffect(() => {
    const setVal = (el, v) => {
      const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
      Object.getOwnPropertyDescriptor(proto, "value").set.call(el, v);
      el.dispatchEvent(new Event("input", { bubbles: true }));
    };
    const onBlur = (e) => {
      const el = e.target;
      if (!(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement)) return;
      if (el.readOnly || el.disabled) return;
      const v = el.value;
      if (!v) return;
      const type = (el.getAttribute("type") || "text").toLowerCase();
      if (type === "email") {
        const fixed = v.toLowerCase();
        if (fixed !== v) setVal(el, fixed);
        return;
      }
      if (["password", "number", "date", "time", "checkbox", "radio", "file", "range", "tel", "color", "hidden"].includes(type)) return;
      if (el instanceof HTMLTextAreaElement) {
        // Texte libre (notes, messages) : 1re lettre en majuscule, le contenu reste respecté
        const fixed = v.charAt(0).toUpperCase() + v.slice(1);
        if (fixed !== v) setVal(el, fixed);
        return;
      }
      const fixed = v.charAt(0).toUpperCase() + v.slice(1).toLowerCase();
      if (fixed !== v) setVal(el, fixed);
    };
    document.addEventListener("focusout", onBlur);
    return () => document.removeEventListener("focusout", onBlur);
  }, []);

  // Règle globale : à l'arrivée sur une page, le curseur se place dans le 1er champ à remplir (en partant du haut)
  useEffect(() => {
    const t = setTimeout(() => {
      const el = document.querySelector(
        'main input:not([type=checkbox]):not([type=radio]):not([type=date]):not([type=time]):not([type=range]):not([type=file]):not([disabled]), main textarea:not([disabled])'
      );
      if (el && el.offsetParent !== null) el.focus({ preventScroll: true });
    }, 800);
    return () => clearTimeout(t);
  }, [location.pathname]);

  const toaster = (
    <Toaster
      position="top-center"
      toastOptions={{
        style: {
          background: "#FFF9F4",
          border: "1px solid #DECDBD",
          color: "#7A5F4A",
          fontFamily: "Inter, sans-serif",
          borderRadius: "18px",
        },
      }}
    />
  );

  if (location.pathname.startsWith("/admin")) {
    return (
      <>
        <Routes>
          <Route path="/admin" element={<Admin />} />
          <Route path="/admin/patientele/:userId" element={<Admin />} />
        </Routes>
        {toaster}
      </>
    );
  }

  // Espace partenaire : pages autonomes, sans les gardes du parcours utilisatrice
  if (location.pathname.startsWith("/partenaire/")) {
    return (
      <>
        <Routes>
          <Route path="/partenaire/inscription" element={<PartnerSignup />} />
          <Route path="/partenaire/espace" element={<PartnerSpace />} />
        </Routes>
        {toaster}
      </>
    );
  }

  const hasProfile = !!localStorage.getItem("rav-profile");
  const editing = new URLSearchParams(location.search).get("edit") === "1";
  const openPaths = ["/", "/bienvenue", "/connexion", "/reservation", "/cgu", "/mentions-legales"];
  // /p/… = liens partenaires publics (clic compté puis redirection) : accessibles sans compte
  const isOpen = openPaths.includes(location.pathname) || location.pathname.startsWith("/visio/") || location.pathname.startsWith("/p/");
  if (!hasProfile && !isOpen) {
    // /instant sans compte : on propose la création de compte (page unique) plutôt que le retour à l'accueil
    const target = location.pathname === "/instant" ? "/connexion?mode=register" : "/";
    return (
      <>
        <Navigate to={target} replace />
        {toaster}
      </>
    );
  }
  if (hasProfile && (location.pathname === "/" || (location.pathname === "/bienvenue" && !editing))) {
    return (
      <>
        <Navigate to="/accueil" replace />
        {toaster}
      </>
    );
  }

  // Bandeaux haut et bas : TOUJOURS présents sur toutes les pages utilisatrices (une fois connectée),
  // séance comprise — pendant la séance ils sont visibles mais INACTIFS (sauf la langue), voir Session.jsx
  // openPaths concerne l'ACCÈS déconnecté, pas l'affichage des barres — ex. /reservation est
  // publique ET garde les barres quand on est connectée (demande Julie : navigation constante).
  const showBars = !!localStorage.getItem("rav-token") && !["/", "/bienvenue", "/connexion"].includes(location.pathname);

  return (
    <div className="julie-shell" data-testid="julie-app-shell">
      {showBars && <TopBar />}
      <Routes>
        <Route path="/" element={<Gate />} />
        <Route path="/bienvenue" element={<Welcome />} />
        <Route path="/accueil" element={<Home />} />
        <Route path="/connexion" element={<Connexion />} />
        <Route path="/profil" element={<Profil />} />
        <Route path="/instant" element={<Instant />} />
        <Route path="/session" element={<Session />} />
        <Route path="/sos" element={<Sos />} />
        <Route path="/mon-instant" element={<MonInstant />} />
        <Route path="/kids" element={<Kids />} />
        <Route path="/explorer" element={<Explorer />} />
        <Route path="/intervenants" element={<Intervenants />} />
        <Route path="/echanger" element={<Echanger />} />
        <Route path="/reservation" element={<Reservation />} />
        <Route path="/cgu" element={<Legal doc="cgu" />} />
        <Route path="/mentions-legales" element={<Legal doc="ml" />} />
        <Route path="/visio/:room" element={<Visio />} />
        <Route path="/offres" element={<Offres />} />
        <Route path="/boutique" element={<Boutique />} />
        <Route path="/mon-accompagnement" element={<MonAccompagnement />} />
        <Route path="/paiement/succes" element={<PaiementSucces />} />
        <Route path="/p/:slug" element={<PartnerLink />} />
      </Routes>
      {showBars && <BottomNav />}
      <InstallPrompt />
      {toaster}
    </div>
  );
}

export default App;
